import {
  uniqBy, debounce, isEqual, pickBy,
} from 'lodash'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import PresentationApi from '@/api/presentation'
import PresentationAttributeApi from '@/api/presentationAttribute'
import constant, { SYSTEM_FIELDS } from '@/constant'

export const EMOJI_AVATAR_ATTR = 'showEmojiAvatar'
export const PARTICIPANT_FIELDS_ATTR = 'participantFields'
export const PARTICIPANT_SELECTED_FIELDS = 'selectedParticipantFields'
export const READINESS_LAST_CHECKED_AT = 'readinessLastCheckedAt'
export const PARTICIPANT_VERIFICATION_ATTR = 'participantVerification'
// Mirrors the plain presentation.autoAssignTeams field as an attribute, because
// the audience client reads presentationAttributes at load but the plain
// presentation field isn't in its sync whitelist.
export const AUTO_ASSIGN_TEAMS_ATTR = 'autoAssignTeams'
const MAX_ALLOWED_FIELD = 8

let debouncedUpdateParticipantField = null
let debouncedUpdateSelectedParticipantField = null

const UPDATE_ATTRIBUTE_MUTATION = 'presentationAttributeModule/updatePresentationAttribute'
const UPDATE_ATTRIBUTE_ACTION = 'presentationAttributeModule/updatePresentationAttributeAction'

const participantVerificationQueues = {}

const participantVerificationQueueFor = (presentationId) => {
  if (!participantVerificationQueues[presentationId]) {
    participantVerificationQueues[presentationId] = {
      tail: Promise.resolve(),
      pendingChanges: [],
      serverConfirmed: null,
    }
  }
  return participantVerificationQueues[presentationId]
}

const projectedParticipantVerification = (queue) => Object.assign({}, queue.serverConfirmed, ...queue.pendingChanges)

const commitParticipantVerification = (commit, value) => {
  commit(UPDATE_ATTRIBUTE_MUTATION, {
    attributeName: PARTICIPANT_VERIFICATION_ATTR,
    attributeValue: value,
  }, { root: true })
}

const removePendingChanges = (queue, changes) => {
  const index = queue.pendingChanges.indexOf(changes)
  if (index !== -1) queue.pendingChanges.splice(index, 1)
}

const actions = {
  updateShowEmojiAvatarSetting: async ({ dispatch }, { presentationId, value }) => {
    dispatch(UPDATE_ATTRIBUTE_ACTION, {
      presentationId,
      payload: {
        attributeName: EMOJI_AVATAR_ATTR,
        attributeValue: value,
      },
    }, { root: true })
  },
  updateParticipantVerificationSetting: async ({ getters, dispatch }, { presentationId, value }) => {
    if (getters.canUseParticipantDomainAllowlist) {
      return dispatch('queueParticipantVerificationWrite', { presentationId, value })
    }
    dispatch(UPDATE_ATTRIBUTE_ACTION, {
      presentationId,
      payload: {
        attributeName: PARTICIPANT_VERIFICATION_ATTR,
        attributeValue: value,
      },
    }, { root: true })
    return undefined
  },
  // Each PATCH carries the whole object, so writes run one at a time and each sends the latest state.
  // The queue is per presentation and the store is touched only while that presentation is still open,
  // because the editor can switch decks while a PATCH is in flight.
  queueParticipantVerificationWrite: ({ getters, commit, rootState }, { presentationId, value }) => {
    const queue = participantVerificationQueueFor(presentationId)
    const isActive = () => rootState.presentation?.id === presentationId
    if (!queue.pendingChanges.length) queue.serverConfirmed = getters.participantVerificationSetting
    const projected = projectedParticipantVerification(queue)
    const changes = pickBy(value, (fieldValue, field) => !isEqual(fieldValue, projected[field]))
    queue.pendingChanges.push(changes)
    if (isActive()) commitParticipantVerification(commit, projectedParticipantVerification(queue))

    const sendLatest = async () => {
      const latest = projectedParticipantVerification(queue)
      try {
        await PresentationAttributeApi.patchPresentationAttribute(presentationId, {
          attributeName: PARTICIPANT_VERIFICATION_ATTR,
          attributeValue: latest,
        })
        queue.serverConfirmed = { ...queue.serverConfirmed, ...latest }
      } catch (error) {
        removePendingChanges(queue, changes)
        if (isActive()) commitParticipantVerification(commit, projectedParticipantVerification(queue))
        throw error
      } finally {
        removePendingChanges(queue, changes)
      }
    }
    const write = queue.tail.then(sendLatest)
    queue.tail = write.catch(() => undefined)
    return write
  },
  loadParticipantVerificationAllowlist: async ({ getters, commit, rootState }, { presentationId }) => {
    const response = await PresentationAttributeApi.getParticipantVerification(presentationId)
    const body = response?.body
    if (!body || !('participantVerification' in body)) return false
    const allowedDomains = body.participantVerification?.allowedDomains ?? []
    if (!Array.isArray(allowedDomains)) return false
    if (rootState.presentation?.id !== presentationId) return false
    const queue = participantVerificationQueueFor(presentationId)
    if (queue.pendingChanges.some((changes) => 'allowedDomains' in changes)) return true
    if (queue.pendingChanges.length) queue.serverConfirmed = { ...queue.serverConfirmed, allowedDomains }
    commitParticipantVerification(commit, { ...getters.participantVerificationSetting, allowedDomains })
    return true
  },
  updateAutoAssignTeamsSetting: async ({ dispatch }, { presentationId, value }) => {
    dispatch(UPDATE_ATTRIBUTE_ACTION, {
      presentationId,
      payload: {
        attributeName: AUTO_ASSIGN_TEAMS_ATTR,
        attributeValue: value,
      },
    }, { root: true })
  },
  updateParticipantFields: async (_, { presentationId, participantFields }) => {
    if (!participantFields || !presentationId) return Promise.resolve()
    const updateAdmission = PresentationApi.updatePresentationByPresentationId(presentationId, {
      audienceAdmission: participantFields,
    })
    const updateParticipantFields = PresentationApi.updateParticipantFields(presentationId, {
      participantFields,
    })

    return Promise.all([updateAdmission, updateParticipantFields])
  },
  updateParticipantInfoFields: async ({ getters, commit, dispatch }, { presentationId, updatedFields }) => {
    const participantInfoSettings = {
      ...getters.participantInfoSettings,
      ...updatedFields,
    }

    commit(UPDATE_ATTRIBUTE_MUTATION, {
      attributeName: PARTICIPANT_FIELDS_ATTR,
      attributeValue: participantInfoSettings,
    }, { root: true })

    if (!debouncedUpdateParticipantField) {
      debouncedUpdateParticipantField = debounce(
        // We need to bind everytime, otherwise, it will catch the wrong value
        // eslint-disable-next-line no-shadow
        (presentationId, participantInfoSettings) => dispatch('updateParticipantFields', { presentationId, participantFields: participantInfoSettings }),
        300,
      )
    }

    return debouncedUpdateParticipantField(presentationId, participantInfoSettings)
  },
  // eslint-disable-next-line no-unused-vars
  updateSelectedParticipantFields: async ({ getters, commit }, { presentationId, fields }) => {
    commit(UPDATE_ATTRIBUTE_MUTATION, {
      attributeName: PARTICIPANT_SELECTED_FIELDS,
      attributeValue: fields,
    }, { root: true })

    if (!debouncedUpdateSelectedParticipantField) {
      debouncedUpdateSelectedParticipantField = debounce(
        // We need to bind everytime, otherwise, it will catch the wrong value
        // eslint-disable-next-line no-shadow
        (presentationId, fields) => {
          PresentationApi.updateSelectedParticipantFields(presentationId, {
            selectedParticipantFields: fields,
          })
        },
        300,
      )
    }

    return debouncedUpdateSelectedParticipantField(presentationId, fields)
  },
  deletePresentationParticipantField: async ({ getters, dispatch }, { presentationId, fieldId }) => {
    const updatedFields = {
      fields: getters.participantFields.filter((f) => f.id !== fieldId),
    }

    dispatch('updateParticipantInfoFields', {
      presentationId,
      updatedFields,
    })

    const updatedSelectedFields = getters.selectedParticipantFieldsRaw.filter((f) => f.id !== fieldId)

    dispatch('updateSelectedParticipantFields', {
      presentationId,
      fields: updatedSelectedFields,
    })
  },
}

const _getters = {
  canUseParticipantVerification: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-29501-participant-verification')
  },
  canUseParticipantDomainAllowlist: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'RPRT-622-participant-domain-allowlist')
  },
  maxAllowedField() {
    return MAX_ALLOWED_FIELD
  },
  participantInfoSettings: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[PARTICIPANT_FIELDS_ATTR] || {}
  },
  selectedParticipantFieldsRaw: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[PARTICIPANT_SELECTED_FIELDS] || []
  },
  readinessLastCheckedAt: (_, __, rootState) => {
    return rootState?.presentationAttributeModule?.presentationAttribute?.[READINESS_LAST_CHECKED_AT] || null
  },

  selectedParticipantFields: (_, getters) => {
    const participantFields = getters?.participantFields
    const selectedParticipantFields = getters?.selectedParticipantFieldsRaw

    return selectedParticipantFields?.map(({ id, required, hidden }) => {
      const fieldData = participantFields?.find(({ id: fieldId }) => fieldId === id)
      return {
        ...fieldData,
        id,
        required,
        hidden,
      }
    })
  },
  participantFields: (_, getters) => {
    const fields = getters?.participantInfoSettings?.fields || []
    const modifiedSystemField = fields.filter((field) => field.fieldType === SYSTEM_FIELDS)
    const systemFields = uniqBy([
      ...modifiedSystemField,
      ...constant.participantSystemFields,
    ], 'id')

    const availableFields = uniqBy([
      ...systemFields,
      ...fields,
    ], 'id')

    return availableFields.map((field) => {
      return {
        ...field,
        isSystemField: field.fieldType === SYSTEM_FIELDS,
      }
    })
  },
  participantFieldHeading: (_, getters) => {
    return getters.participantInfoSettings?.heading ?? 'Welcome to my presentation'
  },
  participantFieldsRequired: (_, getters) => {
    return getters.participantInfoSettings?.isAudienceAdmission || false
  },
  isShownEmojiAvatar: (_, __, rootState) => {
    const attrValue = rootState.presentationAttributeModule?.presentationAttribute?.[EMOJI_AVATAR_ATTR]
    // If undefined it should set to true because it's the current default value
    return attrValue ?? true
  },
  canUseHideCopyResponse: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-36736-hide-copy-response')
  },
  participantVerificationSetting: (_, __, rootState) => {
    // eslint-disable-next-line max-len
    return rootState.presentationAttributeModule?.presentationAttribute?.[PARTICIPANT_VERIFICATION_ATTR] || { enabled: false }
  },
}

export default {
  namespaced: true,
  actions,
  getters: _getters,
}
